import { supabase } from './supabase';
import { useAuth } from '../contexts/AuthContext';

export interface MyFile {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  file_path: string;
  file_name: string;
  file_size: number;
  file_type?: string;
  mime_type?: string;
  folder_path: string;
  tags?: string[];
  category: 'custom' | 'legal' | 'financial' | 'health' | 'pets' | 'travel' | 'digital' | 'legacy' | 'ndis';
  source_module?: string;
  source_id?: string;
  is_encrypted: boolean;
  shared_with?: string[];
  last_accessed?: string;
  created_at: string;
  updated_at: string;
}

export interface MyFolder {
  id: string;
  user_id: string;
  name: string;
  path: string;
  parent_path?: string;
  description?: string;
  color: string;
  icon: string;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface FileUploadOptions {
  folder?: string;
  tags?: string[];
  category?: string;
  description?: string;
}

export interface FolderCreateOptions {
  parentPath?: string;
  description?: string;
  color?: string;
  icon?: string;
}

class MyFilesService {
  // Always fetch the current user from Supabase
  private async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) {
      throw new Error('User not authenticated');
    }
    return user;
  }

  // File Operations
  async uploadFile(file: File, options: FileUploadOptions = {}): Promise<MyFile> {
    const user = await this.getCurrentUser();
    console.log('[MyFilesService] Uploading file. User:', user);
    
    // Validate file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      throw new Error('File size must be less than 100MB');
    }

    const folderPath = options.folder || '/';

    // Sanitize file name: replace spaces and special chars with underscores
    function sanitizeFileName(name: string) {
      return name
        .replace(/[^a-zA-Z0-9._-]/g, '_') // Only allow safe chars
        .replace(/_+/g, '_'); // Collapse multiple underscores
    }

    const safeFileName = sanitizeFileName(file.name);
    const fileName = `${user.id}${folderPath}${Date.now()}_${safeFileName}`;

    // Upload to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('my-files')
      .upload(fileName, file);

    if (uploadError) {
      console.error('[MyFilesService] Supabase storage upload error:', uploadError);
      if (typeof window !== 'undefined') {
        alert('Supabase storage upload error: ' + (uploadError.message || '') + '\n' + (uploadError.details || '') + '\n' + JSON.stringify(uploadError, null, 2));
      }
      throw uploadError;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('my-files')
      .getPublicUrl(fileName);

    // Prepare insert payload with all possible columns and safe defaults
    const insertPayload = {
      user_id: user.id,
      title: options.description || file.name,
      description: options.description || '',
      file_path: fileName,
      file_name: file.name,
      file_size: file.size,
      file_type: file.type || '',
      mime_type: file.type || '',
      folder_path: folderPath,
      tags: options.tags || [],
      category: options.category || 'custom',
      is_encrypted: true,
      shared_with: [],
      last_accessed: null,
      created_at: null,
      updated_at: null,
      source_module: null,
      source_id: null
    };
    console.log('[MyFilesService] Insert payload:', insertPayload);

    // Create database record
    const { data: fileData, error: dbError } = await supabase
      .from('my_files')
      .insert([
        insertPayload
      ])
      .select()
      .single();

    if (dbError) {
      console.error('[MyFilesService] Supabase insert error:', dbError);
      if (typeof window !== 'undefined') {
        alert('Supabase insert error: ' + (dbError.message || '') + '\n' + (dbError.details || '') + '\n' + JSON.stringify(dbError, null, 2) + '\nPayload: ' + JSON.stringify(insertPayload, null, 2));
      }
      throw dbError;
    }

    return fileData;
  }

  async getFiles(folderPath: string = '/', category?: string): Promise<MyFile[]> {
    const user = await this.getCurrentUser();
    
    let query = supabase
      .from('my_files')
      .select('*')
      .eq('user_id', user.id)
      .eq('folder_path', folderPath)
      .order('created_at', { ascending: false });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data || [];
  }

  async getAllFiles(category?: string): Promise<MyFile[]> {
    const user = await this.getCurrentUser();
    
    let query = supabase
      .from('my_files')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data || [];
  }

  async getFileById(id: string): Promise<MyFile> {
    const user = await this.getCurrentUser();
    
    const { data, error } = await supabase
      .from('my_files')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    return data;
  }

  async updateFile(id: string, updates: Partial<MyFile>): Promise<MyFile> {
    const user = await this.getCurrentUser();
    
    const { data, error } = await supabase
      .from('my_files')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteFile(id: string): Promise<void> {
    const user = await this.getCurrentUser();
    
    // Get file info first
    const { data: file, error: fetchError } = await supabase
      .from('my_files')
      .select('file_path')
      .eq('id', id)
      .eq('user_id', user.id)
      .single();

    if (fetchError) throw fetchError;

    // Delete from storage
    if (file.file_path) {
      const { error: storageError } = await supabase.storage
        .from('my-files')
        .remove([file.file_path]);

      if (storageError) throw storageError;
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('my_files')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (dbError) throw dbError;
  }

  async downloadFile(id: string): Promise<Blob> {
    const user = await this.getCurrentUser();
    
    const { data: file, error: fetchError } = await supabase
      .from('my_files')
      .select('file_path')
      .eq('id', id)
      .eq('user_id', user.id)
      .single();

    if (fetchError) throw fetchError;

    const { data, error } = await supabase.storage
      .from('my-files')
      .download(file.file_path);

    if (error) throw error;
    return data;
  }

  // Folder Operations
  async createFolder(name: string, options: FolderCreateOptions = {}): Promise<MyFolder> {
    const user = await this.getCurrentUser();
    
    const parentPath = options.parentPath || '';
    const path = `${parentPath}${name}/`;
    
    // Check if folder already exists
    const { data: existing } = await supabase
      .from('my_folders')
      .select('id')
      .eq('user_id', user.id)
      .eq('path', path)
      .single();

    if (existing) {
      throw new Error('Folder already exists');
    }

    const { data, error } = await supabase
      .from('my_folders')
      .insert([
        {
          user_id: user.id,
          name,
          path,
          parent_path: options.parentPath || null,
          description: options.description,
          color: options.color || '#abd3d2',
          icon: options.icon || 'folder',
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getFolders(parentPath?: string): Promise<MyFolder[]> {
    const user = await this.getCurrentUser();
    
    let query = supabase
      .from('my_folders')
      .select('*')
      .eq('user_id', user.id)
      .order('name', { ascending: true });

    if (parentPath !== undefined) {
      query = query.eq('parent_path', parentPath);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data || [];
  }

  async updateFolder(id: string, updates: Partial<MyFolder>): Promise<MyFolder> {
    const user = await this.getCurrentUser();
    
    const { data, error } = await supabase
      .from('my_folders')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteFolder(id: string): Promise<void> {
    const user = await this.getCurrentUser();
    
    // Check if folder has files
    const { data: files, error: filesError } = await supabase
      .from('my_files')
      .select('id')
      .eq('user_id', user.id)
      .eq('folder_path', (await this.getFolderById(id)).path);

    if (filesError) throw filesError;

    if (files && files.length > 0) {
      throw new Error('Cannot delete folder with files. Please move or delete all files first.');
    }

    // Check if folder has subfolders
    const { data: subfolders, error: subfoldersError } = await supabase
      .from('my_folders')
      .select('id')
      .eq('user_id', user.id)
      .eq('parent_path', (await this.getFolderById(id)).path);

    if (subfoldersError) throw subfoldersError;

    if (subfolders && subfolders.length > 0) {
      throw new Error('Cannot delete folder with subfolders. Please delete all subfolders first.');
    }

    const { error } = await supabase
      .from('my_folders')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  }

  async getFolderById(id: string): Promise<MyFolder> {
    const user = await this.getCurrentUser();
    
    const { data, error } = await supabase
      .from('my_folders')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    return data;
  }

  // Import files from other modules
  async importFromModule(moduleName: string, category: string): Promise<void> {
    const user = await this.getCurrentUser();
    
    // This would be implemented to import files from other modules
    // For now, it's a placeholder that can be expanded based on specific module needs
    console.log(`Importing files from ${moduleName} module`);
  }

  // Search functionality
  async searchFiles(query: string, category?: string): Promise<MyFile[]> {
    const user = await this.getCurrentUser();
    
    let sqlQuery = `
      SELECT * FROM my_files 
      WHERE user_id = '${user.id}' 
      AND (
        title ILIKE '%${query}%' 
        OR description ILIKE '%${query}%' 
        OR file_name ILIKE '%${query}%'
        OR tags::text ILIKE '%${query}%'
      )
    `;

    if (category) {
      sqlQuery += ` AND category = '${category}'`;
    }

    sqlQuery += ' ORDER BY created_at DESC';

    const { data, error } = await supabase.rpc('exec_sql', { sql: sqlQuery });
    if (error) throw error;

    return data || [];
  }

  // Get file statistics
  async getFileStats(): Promise<{
    totalFiles: number;
    totalSize: number;
    byCategory: Record<string, number>;
    byFolder: Record<string, number>;
  }> {
    const user = await this.getCurrentUser();
    
    const { data: files, error } = await supabase
      .from('my_files')
      .select('file_size, category, folder_path')
      .eq('user_id', user.id);

    if (error) throw error;

    const stats = {
      totalFiles: files.length,
      totalSize: files.reduce((sum, file) => sum + (file.file_size || 0), 0),
      byCategory: {} as Record<string, number>,
      byFolder: {} as Record<string, number>,
    };

    files.forEach(file => {
      stats.byCategory[file.category] = (stats.byCategory[file.category] || 0) + 1;
      stats.byFolder[file.folder_path] = (stats.byFolder[file.folder_path] || 0) + 1;
    });

    return stats;
  }

  // Sync files from other modules
  async syncModuleFiles(): Promise<void> {
    const user = await this.getCurrentUser();
    
    const { error } = await supabase.rpc('sync_module_files_to_my_files', {
      user_uuid: user.id
    });
    
    if (error) throw error;
  }

  // Initialize user folders
  async initializeUserFolders(): Promise<void> {
    const user = await this.getCurrentUser();
    
    const { error } = await supabase.rpc('initialize_user_folders', {
      user_uuid: user.id
    });
    
    if (error) throw error;
  }

  // Get user storage usage
  async getStorageUsage(): Promise<{
    totalUsed: number;
    maxStorage: number;
    percentageUsed: number;
  }> {
    const user = await this.getCurrentUser();
    
    const { data, error } = await supabase.rpc('get_user_storage_usage', {
      user_uuid: user.id
    });
    
    if (error) throw error;
    
    return {
      totalUsed: data[0]?.total_used || 0,
      maxStorage: data[0]?.max_storage || 16106127360, // 15GB
      percentageUsed: data[0]?.percentage_used || 0
    };
  }

  // Check if user can upload a file of given size
  async canUploadFile(fileSize: number): Promise<{
    canUpload: boolean;
    currentUsage: number;
    maxStorage: number;
    remainingSpace: number;
  }> {
    const user = await this.getCurrentUser();
    
    try {
      const { data, error } = await supabase.rpc('check_user_storage_limit', {
        user_uuid: user.id,
        new_file_size: fileSize
      });
      
      if (error) {
        // If there's an error, it means the limit would be exceeded
        const usage = await this.getStorageUsage();
        return {
          canUpload: false,
          currentUsage: usage.totalUsed,
          maxStorage: usage.maxStorage,
          remainingSpace: usage.maxStorage - usage.totalUsed
        };
      }
      
      const usage = await this.getStorageUsage();
      return {
        canUpload: true,
        currentUsage: usage.totalUsed,
        maxStorage: usage.maxStorage,
        remainingSpace: usage.maxStorage - usage.totalUsed
      };
    } catch (err) {
      const usage = await this.getStorageUsage();
      return {
        canUpload: false,
        currentUsage: usage.totalUsed,
        maxStorage: usage.maxStorage,
        remainingSpace: usage.maxStorage - usage.totalUsed
      };
    }
  }
}

export const myFilesService = new MyFilesService(); 